import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import Config from '@qover/utils/src/Config';
import { customWindow } from '@qover/utils/src/customWindow';

const IssueReporterButton = styled.button`
  background-color: #317bda;
  color: white;
  cursor: pointer;
  opacity: 0.7;
  padding: 10px 20px;
  position: fixed;
  right: 0;
  top: 170px;
  transform: rotate(-90deg);
  transform-origin: bottom right;
  z-index: 9999;
`;

function getViewport() {
  let viewPortWidth;
  let viewPortHeight;

  /**
   * the more standards compliant browsers (mozilla/netscape/opera/IE7)
   * use window.innerWidth and window.innerHeight
   */
  if (typeof window.innerWidth !== 'undefined') {
    viewPortWidth = window.innerWidth;
    viewPortHeight = window.innerHeight;
  } else if (
    // IE6 in standards compliant mode (i.e. with a valid doctype as the first line in the document)
    typeof document.documentElement !== 'undefined' &&
    typeof document.documentElement.clientWidth !== 'undefined' &&
    document.documentElement.clientWidth !== 0
  ) {
    viewPortWidth = document.documentElement.clientWidth;
    viewPortHeight = document.documentElement.clientHeight;
  } else {
    // older versions of IE
    viewPortWidth = document.getElementsByTagName('body')[0].clientWidth;
    viewPortHeight = document.getElementsByTagName('body')[0].clientHeight;
  }
  return [viewPortWidth, viewPortHeight];
}

const description = () => {
  const viewport = getViewport();
  const userAgent = navigator.userAgent;

  return `*Steps to Reproduce*
{color:#cccccc}¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯{color}


*Expected Behavior*
{color:#cccccc}¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯{color}


*Current Behavior*
{color:#cccccc}¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯{color}


*Possible Solution*
{color:#cccccc}¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯{color}


*Detailed Description*
{color:#cccccc}¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯{color}

*Environment*
{color:#cccccc}¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯¯{color}
*user agent*: ${userAgent}.
*viewport*: ${viewport[0]} x ${viewport[1]}.
*location*: ${document.URL}.

`;
};
const product = {
  name: 'customfield_10621',
  values: {
    ADMIN: '10138',
    ALL: '10130',
    B2C: '10170',
    BKFR: '10169',
    CWBE: '10168',
    HIBE: '10167',
    LLBE: '10117',
    NONE: '-1',
  },
};

const classification = {
  name: 'customfield_10601',
  values: {
    frontend: '10101',
    protect: '10171',
  },
};


type JiraIssueReporterProps = {
  classifications?: Array<keyof typeof classification.values>;
  productCode?: string;
  summaryTag: string;
};

function JiraIssueReporter(props: JiraIssueReporterProps) {
  if (Config.env === 'production') {
    return null;
  }
  const { classifications = [], productCode = 'NONE' } = props;
  const issueReporterButtonRef = useRef(() => {});
  const [buttonDisabled, setButtonDisabled] = useState(true);
  useEffect(() => {
    customWindow.ATL_JQ_PAGE_PROPS = {
      ...(customWindow.ATL_JQ_PAGE_PROPS || {}),
      triggerFunction: (showCollectorDialog: () => void) => {
        issueReporterButtonRef.current = showCollectorDialog;
      },
      fieldValues: {
        summary: `[${props.summaryTag}]${
          productCode !== 'NONE' ? `[${productCode}]` : ''
        } - `,
        description: description(),
        [product.name]:
          product.values[productCode as keyof typeof product.values],
        [classification.name]: [
          classification.values.frontend,
          ...classifications.map(
            (v: keyof typeof classification.values) => classification.values[v],
          ),
        ],
        environment: JSON.stringify(
          {
            configFile: `${Config.env}.json`,
          },
          null,
          2,
        ),
        timetracking_originalestimate: '1h',
      },
    };
  }, [productCode]);
  useEffect(() => {
    const jiraScript = document.getElementById('jira-issue-collector');
    if (!jiraScript) {
      const script = document.createElement('script');
      script.src =
        'https://qover001.atlassian.net/s/d41d8cd98f00b204e9800998ecf8427e-T/-w86obp/b/11/a44af77267a987a660377e5c46e0fb64/_/download/batch/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector.js?locale=en-US&collectorId=8011bca3';
      script.id = 'jira-issue-collector';
      script.async = true;
      script.onload = () => {
        setButtonDisabled(false);
      };
      document.getElementsByTagName('head')[0].appendChild(script);
    }
  }, []);
  return (
    <IssueReporterButton
      disabled={buttonDisabled}
      onClick={() => {
        if (issueReporterButtonRef.current) {
          issueReporterButtonRef.current();
        }
      }}
      type="button"
    >
      Issue reporter
    </IssueReporterButton>
  );
}

export default JiraIssueReporter;
